// Reads the WSTSC YouTube channel feed through /youtube-feed.php (see
// public/youtube-feed.php; in `npm run dev` Vite proxies the same path) and
// turns it into the video objects the homepage social section renders.

import { youtubeFeed, channelVideos } from "../constants/social.js";
import { ORG } from "../seo/site.js";

const NS = {
  atom: "http://www.w3.org/2005/Atom",
  yt: "http://www.youtube.com/xml/schemas/2015",
  media: "http://search.yahoo.com/mrss/",
};

const catalogue = new Map(channelVideos.map((video) => [video.id, video]));

// YouTube timestamps are UTC; show the date it was in Sydney when published
// (an evening upload in Sydney is already the next day in UTC terms).
const sydneyFormat = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Australia/Sydney",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
export const sydneyDate = (isoTimestamp) => {
  const date = new Date(isoTimestamp);
  return Number.isNaN(date.getTime()) ? "" : sydneyFormat.format(date);
};

const text = (parent, ns, tag) =>
  parent.getElementsByTagNameNS(ns, tag)[0]?.textContent?.trim() || "";

// A short summary from the video's own description when WSTSC hasn't written
// one in channelVideos: prefer an English line (many WSTSC descriptions have
// one) so non-Tamil readers know what the video is, else the first line.
// Hashtags and very short lines are skipped.
const summaryFrom = (description = "") => {
  const lines = description
    .split(/\n+/)
    .map((line) => line.replace(/#[^\s#]+/g, "").trim())
    .filter((line) => line.length > 20);
  const english = lines.find((line) => /[A-Za-z]{3}/.test(line) && !/[\u0B80-\u0BFF]/.test(line));
  const line = english || lines[0] || "";
  const sentence = line.match(/^.{20,}?[.!?](\s|$)/)?.[0]?.trim() || line;
  return sentence.length > 160 ? `${sentence.slice(0, 157).trimEnd()}…` : sentence;
};

export const parseFeed = (xml) => {
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  if (doc.getElementsByTagName("parsererror").length) throw new Error("Invalid feed XML");

  return [...doc.getElementsByTagNameNS(NS.atom, "entry")].map((entry) => {
    const link = entry.getElementsByTagNameNS(NS.atom, "link")[0]?.getAttribute("href") || "";
    return {
      id: text(entry, NS.yt, "videoId"),
      channelId: text(entry, NS.yt, "channelId"),
      title: text(entry, NS.atom, "title"),
      date: sydneyDate(text(entry, NS.atom, "published")),
      description: text(entry, NS.media, "description"),
      isShort: link.includes("/shorts/"),
    };
  });
};

// Applies WSTSC's editorial settings: own channel only, hidden videos removed,
// optional Shorts filter, pinned video first, and the English summary and alt
// text from channelVideos (or generated from the description for new uploads).
export const selectVideos = (entries) => {
  const hidden = new Set(youtubeFeed.hiddenVideoIds);
  const videos = entries
    .filter((video) => video.id && video.channelId === ORG.social.youtubeChannelId)
    .filter((video) => !hidden.has(video.id))
    .filter((video) => youtubeFeed.includeShorts || !video.isShort)
    .map((video) => {
      const known = catalogue.get(video.id);
      return {
        ...video,
        date: video.date || known?.date || "",
        summary: known?.summary || summaryFrom(video.description),
        thumbnailAlt: known?.thumbnailAlt || `Video thumbnail: ${video.title}`,
      };
    });

  // The feed only lists the newest 15 uploads, so older videos saved in
  // channelVideos are added after them (newest first overall).
  const listed = new Set(videos.map((video) => video.id));
  for (const video of channelVideos) {
    if (!listed.has(video.id) && !hidden.has(video.id)) {
      videos.push({ ...video, channelId: ORG.social.youtubeChannelId, isShort: false });
    }
  }
  videos.sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  const pinnedId = youtubeFeed.pinnedVideoId;
  const pinned = videos.findIndex((video) => video.id === pinnedId);
  if (pinned > 0) videos.unshift(...videos.splice(pinned, 1));

  // No limit set = show every video (the list scrolls, 4 at a time).
  return youtubeFeed.limit ? videos.slice(0, youtubeFeed.limit) : videos;
};

// channelVideos in the same shape as parsed feed entries (used as fallback).
export const catalogueEntries = () =>
  channelVideos.map((video) => ({
    ...video,
    channelId: ORG.social.youtubeChannelId,
    description: "",
    isShort: false,
  }));

export const fetchChannelVideos = async ({ timeoutMs = 8000 } = {}) => {
  const response = await fetch(youtubeFeed.endpoint, {
    headers: { Accept: "application/xml" },
    signal: AbortSignal.timeout(timeoutMs),
  });
  if (!response.ok) throw new Error(`Feed HTTP ${response.status}`);
  return selectVideos(parseFeed(await response.text()));
};
